import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  AlertCircle,
  GripVertical,
  ArrowUpDown,
  Fuel,
  Download,
  Upload,
  CheckCircle,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import {
  useFleetDefaultTruckTypes,
  useFleetDefaultMakesModels,
  downloadFleetDefaultsJson,
  uploadFleetDefaultsJson,
  type FleetDefaultTruckType,
  type FleetDefaultMakeModel,
  type TruckTypeInput,
  type MakeModelInput,
  type FuelType,
  type UploadSummary,
} from '../../hooks/useFleetDefaults';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Combobox } from '../../components/ui/Combobox';

// ── Helpers ───────────────────────────────────────────────────────────────

function FieldLabel({ htmlFor, children, optional }: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-widest text-slate">
      {children}
      {!optional && <span className="text-rose ml-0.5">*</span>}
      {optional && <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>}
    </label>
  );
}

// ── TruckTypeForm ─────────────────────────────────────────────────────────

interface TruckTypeFormProps {
  initial?: FleetDefaultTruckType;
  onClose: () => void;
  onSave: (input: TruckTypeInput) => Promise<unknown>;
}

function TruckTypeForm({ initial, onClose, onSave }: TruckTypeFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [capacity, setCapacity] = useState(String(initial?.defaultCapacity ?? ''));
  const [weight, setWeight] = useState(String(initial?.defaultWeightLimit ?? ''));
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        defaultCapacity: Number(capacity) || 0,
        defaultWeightLimit: Number(weight) || 0,
        active,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-charcoal rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-md md:w-full md:max-h-[80vh] md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-lg font-bold text-midnight dark:text-white">
            {initial ? 'Edit Truck Type' : 'Add Truck Type'}
          </h2>
          <button onClick={onClose} className="p-1 text-slate hover:text-midnight dark:hover:text-white transition-colors" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-rose/10 border border-rose/30">
              <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
              <p className="text-[13px] text-rose">{error}</p>
            </div>
          )}

          <div>
            <FieldLabel htmlFor="tt-name">Name</FieldLabel>
            <input
              id="tt-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. 26ft Box Truck"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-stone bg-white dark:bg-midnight dark:border-slate/30
                         text-small text-midnight dark:text-white placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel htmlFor="tt-capacity">Default Capacity (cu ft)</FieldLabel>
              <input
                id="tt-capacity"
                type="number"
                min="0"
                value={capacity}
                onChange={e => setCapacity(e.target.value)}
                placeholder="0"
                className="mt-1 w-full h-11 px-3 rounded-lg border border-stone bg-white dark:bg-midnight dark:border-slate/30
                           text-small text-midnight dark:text-white placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
            </div>
            <div>
              <FieldLabel htmlFor="tt-weight">Default Weight (lbs)</FieldLabel>
              <input
                id="tt-weight"
                type="number"
                min="0"
                value={weight}
                onChange={e => setWeight(e.target.value)}
                placeholder="0"
                className="mt-1 w-full h-11 px-3 rounded-lg border border-stone bg-white dark:bg-midnight dark:border-slate/30
                           text-small text-midnight dark:text-white placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <FieldLabel htmlFor="tt-active">Active</FieldLabel>
            <button
              id="tt-active"
              type="button"
              role="switch"
              aria-checked={active}
              onClick={() => setActive(!active)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200
                ${active ? 'bg-plum' : 'bg-stone'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200
                ${active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full h-12 rounded-xl bg-plum text-white text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150"
          >
            {saving ? 'Saving...' : initial ? 'Save Changes' : 'Add Truck Type'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── TruckTypeCard ─────────────────────────────────────────────────────────

interface TruckTypeCardProps {
  item: FleetDefaultTruckType;
  editingOrder: boolean;
  isDragOver: boolean;
  isDragging: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function TruckTypeCard({
  item,
  editingOrder,
  isDragOver,
  isDragging,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: TruckTypeCardProps) {
  return (
    <div
      draggable={editingOrder}
      onDragStart={editingOrder ? onDragStart : undefined}
      onDragOver={editingOrder ? onDragOver : undefined}
      onDrop={editingOrder ? onDrop : undefined}
      onDragEnd={editingOrder ? onDragEnd : undefined}
      className={`bg-white dark:bg-charcoal border rounded-xl px-4 py-3 shadow-brand-sm select-none
        transition-all duration-100
        ${isDragOver ? 'border-plum border-2' : 'border-stone dark:border-slate/30'}
        ${isDragging ? 'opacity-40' : 'opacity-100'}`}
    >
      <div className="flex items-center gap-3">
        {editingOrder && (
          <div className="shrink-0 text-slate hover:text-midnight cursor-grab active:cursor-grabbing">
            <GripVertical size={18} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-[14px] text-midnight dark:text-white truncate">{item.name}</p>
            {!item.active && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-stone/60 text-slate rounded">
                Inactive
              </span>
            )}
          </div>
          <p className="text-[13px] text-slate">
            {item.defaultCapacity.toLocaleString()} cu ft &middot; {item.defaultWeightLimit.toLocaleString()} lbs
          </p>
        </div>

        {!editingOrder && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onEdit}
              className="p-2 text-slate hover:text-plum transition-colors"
              aria-label="Edit"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-slate hover:text-rose transition-colors"
              aria-label="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MakeModelForm ─────────────────────────────────────────────────────────

const FUEL_TYPE_OPTIONS: { value: FuelType; label: string }[] = [
  { value: 'gas', label: 'Gas' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Electric' },
  { value: 'hybrid', label: 'Hybrid' },
];

interface MakeModelFormProps {
  initial?: FleetDefaultMakeModel;
  truckTypes: FleetDefaultTruckType[];
  existingMakes: string[];
  onClose: () => void;
  onSave: (input: MakeModelInput) => Promise<unknown>;
}

function MakeModelForm({ initial, truckTypes, existingMakes, onClose, onSave }: MakeModelFormProps) {
  const [make, setMake] = useState(initial?.make ?? '');
  const [model, setModel] = useState(initial?.model ?? '');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initial?.compatibleTruckTypes ?? []);
  const [selectedFuelTypes, setSelectedFuelTypes] = useState<FuelType[]>(initial?.fuelTypeDefaults ?? []);
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  function toggleType(id: string) {
    setSelectedTypes(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!make.trim()) { setError('Make is required.'); return; }
    if (!model.trim()) { setError('Model is required.'); return; }
    if (selectedTypes.length === 0) { setError('Select at least one compatible truck type.'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({
        make: make.trim(),
        model: model.trim(),
        compatibleTruckTypes: selectedTypes,
        fuelTypeDefaults: selectedFuelTypes,
        active,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-charcoal rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-md md:w-full md:max-h-[80vh] md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-lg font-bold text-midnight dark:text-white">
            {initial ? 'Edit Make/Model' : 'Add Make/Model'}
          </h2>
          <button onClick={onClose} className="p-1 text-slate hover:text-midnight dark:hover:text-white transition-colors" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-rose/10 border border-rose/30">
              <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
              <p className="text-[13px] text-rose">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel htmlFor="mm-make">Make</FieldLabel>
              <Combobox
                id="mm-make"
                value={make}
                onChange={setMake}
                options={existingMakes.map((m, i) => ({ id: i, name: m }))}
                placeholder="e.g. Ford"
                className="mt-1"
              />
            </div>
            <div>
              <FieldLabel htmlFor="mm-model">Model</FieldLabel>
              <input
                id="mm-model"
                type="text"
                autoComplete="off"
                value={model}
                onChange={e => setModel(e.target.value)}
                placeholder="e.g. E-450"
                className="mt-1 w-full h-11 px-3 rounded-lg border border-stone bg-white dark:bg-midnight dark:border-slate/30
                           text-small text-midnight dark:text-white placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
              />
            </div>
          </div>

          {/* Compatible Truck Types multi-select */}
          <div>
            <FieldLabel htmlFor="mm-types">Compatible Truck Types</FieldLabel>
            <div className="mt-1 relative">
              <button
                id="mm-types"
                type="button"
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white dark:bg-midnight dark:border-slate/30
                           text-small text-midnight dark:text-white text-left flex items-center justify-between
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
              >
                <span className={selectedTypes.length === 0 ? 'text-slate' : ''}>
                  {selectedTypes.length === 0
                    ? 'Select types...'
                    : `${selectedTypes.length} selected`}
                </span>
                <ChevronDown size={14} className="text-slate" />
              </button>
              {showTypeDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-charcoal border border-stone dark:border-slate/30 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {truckTypes.filter(t => t.active).map(tt => (
                    <label
                      key={tt.id}
                      className="flex items-center gap-2 px-3 py-2.5 hover:bg-mist dark:hover:bg-midnight cursor-pointer text-small"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(tt.id)}
                        onChange={() => toggleType(tt.id)}
                        className="rounded border-stone text-plum focus:ring-plum/20"
                      />
                      <span className="text-midnight dark:text-white">{tt.name}</span>
                    </label>
                  ))}
                  {truckTypes.filter(t => t.active).length === 0 && (
                    <p className="px-3 py-2.5 text-[13px] text-slate">No active truck types</p>
                  )}
                </div>
              )}
            </div>
            {selectedTypes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedTypes.map(id => {
                  const tt = truckTypes.find(t => t.id === id);
                  return tt ? (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-plum/10 text-plum rounded-md"
                    >
                      {tt.name}
                      <button type="button" onClick={() => toggleType(id)} className="hover:text-rose">
                        <X size={10} />
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Fuel type */}
          <div>
            <FieldLabel htmlFor="mm-fuel" optional>Default Fuel Types</FieldLabel>
            <div className="mt-1 flex bg-mist dark:bg-midnight rounded-lg p-1 gap-1">
              {FUEL_TYPE_OPTIONS.map(ft => (
                <button
                  key={ft.value}
                  type="button"
                  onClick={() => setSelectedFuelTypes(prev =>
                    prev.includes(ft.value) ? prev.filter(f => f !== ft.value) : [...prev, ft.value],
                  )}
                  className={`flex-1 py-2 text-[12px] font-semibold rounded-md transition-all duration-150
                    ${selectedFuelTypes.includes(ft.value)
                      ? 'bg-white dark:bg-charcoal text-midnight dark:text-white shadow-brand-sm'
                      : 'text-slate hover:text-midnight dark:hover:text-white'
                    }`}
                >
                  {ft.label}
                </button>
              ))}
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between">
            <FieldLabel htmlFor="mm-active">Active</FieldLabel>
            <button
              id="mm-active"
              type="button"
              role="switch"
              aria-checked={active}
              onClick={() => setActive(!active)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200
                ${active ? 'bg-plum' : 'bg-stone'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200
                ${active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full h-12 rounded-xl bg-plum text-white text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150"
          >
            {saving ? 'Saving...' : initial ? 'Save Changes' : 'Add Make/Model'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── MakeModelCard ─────────────────────────────────────────────────────────

interface MakeModelCardProps {
  item: FleetDefaultMakeModel;
  truckTypes: FleetDefaultTruckType[];
  onEdit: () => void;
  onDelete: () => void;
}

function MakeModelCard({ item, truckTypes, onEdit, onDelete }: MakeModelCardProps) {
  const typeNames = item.compatibleTruckTypes
    .map(id => truckTypes.find(t => t.id === id)?.name)
    .filter(Boolean);

  return (
    <div className="bg-white dark:bg-charcoal border border-stone dark:border-slate/30 rounded-xl px-4 py-3 shadow-brand-sm">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-[14px] text-midnight dark:text-white truncate">
              {item.make} {item.model}
            </p>
            {!item.active && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-stone/60 text-slate rounded">
                Inactive
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {typeNames.map(name => (
              <span key={name} className="px-1.5 py-0.5 text-[10px] font-semibold bg-plum/10 text-plum rounded">
                {name}
              </span>
            ))}
          </div>
          {item.fuelTypeDefaults.length > 0 && (
            <div className="flex items-center gap-1 mt-1.5">
              <Fuel size={11} className="text-slate" />
              <span className="text-[12px] text-slate capitalize">{item.fuelTypeDefaults.join(', ')}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} className="p-2 text-slate hover:text-plum transition-colors" aria-label="Edit">
            <Pencil size={14} />
          </button>
          <button onClick={onDelete} className="p-2 text-slate hover:text-rose transition-colors" aria-label="Delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MakesModelsSection ────────────────────────────────────────────────────

interface MakesModelsSectionProps {
  truckTypes: FleetDefaultTruckType[];
}

function MakesModelsSection({ truckTypes }: MakesModelsSectionProps) {
  const { items, loading, error, createItem, updateItem, deleteItem } = useFleetDefaultMakesModels();

  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const [makeFilter, setMakeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<FleetDefaultMakeModel | null>(null);
  const [deletingItem, setDeletingItem] = useState<FleetDefaultMakeModel | null>(null);

  // Distinct makes (sorted alphabetically) for filter dropdown and form combobox
  const distinctMakes = useMemo(() => {
    const seen = new Set<string>();
    return items
      .map(m => m.make)
      .filter(m => { if (seen.has(m)) return false; seen.add(m); return true; })
      .sort((a, b) => a.localeCompare(b));
  }, [items]);

  // Filter
  let filtered = items;
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(m =>
      m.make.toLowerCase().includes(q) || m.model.toLowerCase().includes(q),
    );
  }
  if (makeFilter) {
    filtered = filtered.filter(m => m.make === makeFilter);
  }
  if (typeFilter) {
    filtered = filtered.filter(m => m.compatibleTruckTypes.includes(typeFilter));
  }

  async function handleCreate(input: MakeModelInput) {
    await createItem(input);
  }

  async function handleUpdate(input: MakeModelInput) {
    if (!editingItem) return;
    await updateItem(editingItem.id, input);
  }

  async function handleDelete() {
    if (!deletingItem) return;
    await deleteItem(deletingItem.id);
    setDeletingItem(null);
  }

  return (
    <div className="mt-10">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 w-full text-left mb-4 group"
      >
        <ChevronRight size={16} className={`text-slate transition-transform duration-150 ${collapsed ? '' : 'rotate-90'}`} />
        <h2 className="font-display text-base font-bold text-midnight dark:text-white">Makes &amp; Models</h2>
        <span className="text-[12px] text-slate font-normal">({items.length})</span>
      </button>

      {collapsed ? null : (<>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[120px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full h-11 pl-9 pr-3 rounded-xl border border-stone dark:border-slate/30 bg-white dark:bg-midnight
                       text-small text-midnight dark:text-white placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
          />
        </div>
        <select
          value={makeFilter}
          onChange={e => setMakeFilter(e.target.value)}
          className="h-11 px-3 rounded-xl border border-stone dark:border-slate/30 bg-white dark:bg-midnight
                     text-small text-midnight dark:text-white
                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
        >
          <option value="">All makes</option>
          {distinctMakes.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="h-11 px-3 rounded-xl border border-stone dark:border-slate/30 bg-white dark:bg-midnight
                     text-small text-midnight dark:text-white
                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
        >
          <option value="">All types</option>
          {truckTypes.filter(t => t.active).map(tt => (
            <option key={tt.id} value={tt.id}>{tt.name}</option>
          ))}
        </select>
        <button
          onClick={() => setShowAddForm(true)}
          className="shrink-0 h-11 w-11 rounded-xl bg-plum text-white flex items-center justify-center
                     hover:bg-rose active:opacity-80 transition-colors duration-150"
        >
          <Plus size={20} />
        </button>
      </div>

      {/* States */}
      {loading && <LoadingSpinner />}

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{error}</p>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-center py-8">
          <p className="text-slate text-small mb-3">No makes/models configured yet.</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-plum text-white text-small font-semibold
                       hover:bg-rose transition-colors duration-150"
          >
            <Plus size={16} /> Add Make/Model
          </button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && items.length > 0 && (
        <p className="text-center py-6 text-slate text-small">No results matching your filters.</p>
      )}

      {/* List */}
      {!loading && !error && filtered.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
            {filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}
            {search && ` matching "${search}"`}
          </p>
          <div className="flex flex-col gap-2">
            {filtered.map(item => (
              <MakeModelCard
                key={item.id}
                item={item}
                truckTypes={truckTypes}
                onEdit={() => setEditingItem(item)}
                onDelete={() => setDeletingItem(item)}
              />
            ))}
          </div>
        </>
      )}

      </>)}

      {/* Modals */}
      {showAddForm && (
        <MakeModelForm
          truckTypes={truckTypes}
          existingMakes={distinctMakes}
          onClose={() => setShowAddForm(false)}
          onSave={handleCreate}
        />
      )}

      {editingItem && (
        <MakeModelForm
          initial={editingItem}
          truckTypes={truckTypes}
          existingMakes={distinctMakes}
          onClose={() => setEditingItem(null)}
          onSave={handleUpdate}
        />
      )}

      <ConfirmModal
        isOpen={!!deletingItem}
        title="Delete Make/Model"
        message={deletingItem ? `Are you sure you want to delete "${deletingItem.make} ${deletingItem.model}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeletingItem(null)}
      />
    </div>
  );
}

// ── Main Content ──────────────────────────────────────────────────────────

function FleetDefaultsContent() {
  const navigate = useNavigate();
  const { items, loading, error, createItem, updateItem, deleteItem, reorderItems, refetch } = useFleetDefaultTruckTypes();

  const [truckTypesCollapsed, setTruckTypesCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<FleetDefaultTruckType | null>(null);
  const [deletingItem, setDeletingItem] = useState<FleetDefaultTruckType | null>(null);

  // Reorder state
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderedItems, setOrderedItems] = useState<FleetDefaultTruckType[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Download/upload state
  const [uploadResult, setUploadResult] = useState<UploadSummary | null>(null);
  const [uploadError, setUploadError] = useState('');
  async function handleDownload() {
    try {
      await downloadFleetDefaultsJson();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Download failed');
    }
  }

  async function handleUploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadError('');
    setUploadResult(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const result = await uploadFleetDefaultsJson(data);
      setUploadResult(result);
      refetch();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed. Check the JSON format.');
    }
  }

  // Filter
  const displayItems = editingOrder ? orderedItems : items;
  const filtered = displayItems.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  // Reorder handlers
  function enterEditOrder() {
    setOrderedItems([...items]);
    setEditingOrder(true);
    setSearch('');
  }

  function cancelEditOrder() {
    setEditingOrder(false);
    setOrderedItems([]);
    setDraggedId(null);
    setDragOverId(null);
  }

  async function saveOrder() {
    setSavingOrder(true);
    try {
      await reorderItems(orderedItems.map(t => t.id));
      setEditingOrder(false);
      setOrderedItems([]);
    } finally {
      setSavingOrder(false);
    }
  }

  // DnD handlers
  function handleDragStart(e: React.DragEvent, id: string) {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) setDragOverId(id);
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    const sourceId = draggedId ?? e.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    setOrderedItems(prev => {
      const result = [...prev];
      const srcIdx = result.findIndex(t => t.id === sourceId);
      const tgtIdx = result.findIndex(t => t.id === targetId);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      const [moved] = result.splice(srcIdx, 1);
      result.splice(tgtIdx, 0, moved);
      return result;
    });

    setDraggedId(null);
    setDragOverId(null);
  }

  function handleDragEnd() {
    setDraggedId(null);
    setDragOverId(null);
  }

  // CRUD handlers
  async function handleCreate(input: TruckTypeInput) {
    await createItem(input);
  }

  async function handleUpdate(input: TruckTypeInput) {
    if (!editingItem) return;
    await updateItem(editingItem.id, input);
  }

  async function handleDelete() {
    if (!deletingItem) return;
    await deleteItem(deletingItem.id);
    setDeletingItem(null);
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-3xl md:mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate('/admin/platform-tools/defaults')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight dark:hover:text-white mb-4 -ml-1 transition-colors duration-150"
      >
        <ChevronLeft size={16} />
        Platform Defaults
      </button>

      {/* Header */}
      <h1 className="font-display text-[28px] font-bold leading-tight text-midnight dark:text-white mb-1">Fleet Defaults</h1>
      <p className="text-[13px] text-slate mb-4 leading-snug">
        Manage truck types and default specs for fleet management across all organizations.
      </p>

      {/* Download / Upload buttons */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-stone dark:border-slate/30
                     bg-white dark:bg-midnight text-small font-medium text-slate
                     hover:border-plum hover:text-plum active:bg-mist transition-colors duration-150"
        >
          <Download size={15} /> Download JSON
        </button>
        <label
          className="inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-stone dark:border-slate/30
                     bg-white dark:bg-midnight text-small font-medium text-slate cursor-pointer
                     hover:border-plum hover:text-plum active:bg-mist transition-colors duration-150"
        >
          <Upload size={15} /> Upload JSON
          <input type="file" accept=".json" onChange={handleUploadFile} className="hidden" />
        </label>
      </div>

      {/* Upload result banner */}
      {uploadResult && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <CheckCircle size={15} className="shrink-0 text-emerald-600 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-small text-emerald-800 dark:text-emerald-200">
              Truck types: {uploadResult.truckTypesCreated} created, {uploadResult.truckTypesUpdated} updated.
              Makes/models: {uploadResult.makesModelsCreated} created, {uploadResult.makesModelsUpdated} updated.
            </p>
            {uploadResult.errors.length > 0 && (
              <ul className="mt-1 text-[12px] text-amber-700 dark:text-amber-300 list-disc list-inside">
                {uploadResult.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            )}
          </div>
          <button onClick={() => setUploadResult(null)} className="text-emerald-600 hover:text-emerald-800" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      {uploadError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="flex-1 text-small text-rose">{uploadError}</p>
          <button onClick={() => setUploadError('')} className="text-rose" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Collapsible section header */}
      <button
        type="button"
        onClick={() => setTruckTypesCollapsed(!truckTypesCollapsed)}
        className="flex items-center gap-2 w-full text-left mb-4 group"
      >
        <ChevronRight size={16} className={`text-slate transition-transform duration-150 ${truckTypesCollapsed ? '' : 'rotate-90'}`} />
        <h2 className="font-display text-base font-bold text-midnight dark:text-white">Truck Types</h2>
        <span className="text-[12px] text-slate font-normal">({items.length})</span>
      </button>

      {truckTypesCollapsed ? null : (<>
      {/* Toolbar */}
      {!editingOrder ? (
        <div className="flex items-center gap-2 mb-5">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search truck types..."
              className="w-full h-11 pl-9 pr-3 rounded-xl border border-stone dark:border-slate/30 bg-white dark:bg-midnight
                         text-small text-midnight dark:text-white placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
          <button
            onClick={enterEditOrder}
            disabled={items.length < 2}
            title="Reorder"
            className="shrink-0 h-11 w-11 rounded-xl border border-stone dark:border-slate/30 bg-white dark:bg-midnight
                       flex items-center justify-center text-slate hover:border-plum hover:text-plum
                       active:bg-mist disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
          >
            <ArrowUpDown size={16} />
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="shrink-0 h-11 w-11 rounded-xl bg-plum text-white flex items-center justify-center
                       hover:bg-rose active:opacity-80 transition-colors duration-150"
          >
            <Plus size={20} />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <p className="flex-1 text-small text-slate">
            Drag to reorder. This controls the order in dropdowns.
          </p>
          <button
            onClick={cancelEditOrder}
            disabled={savingOrder}
            className="h-11 px-4 rounded-xl border border-stone bg-white text-small font-semibold text-slate
                       hover:border-rose hover:text-rose active:bg-mist disabled:opacity-50 transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            onClick={saveOrder}
            disabled={savingOrder}
            className="h-11 px-4 rounded-xl bg-plum text-white text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150"
          >
            {savingOrder ? 'Saving...' : 'Save Order'}
          </button>
        </div>
      )}

      {/* States */}
      {loading && <LoadingSpinner />}

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{error}</p>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate text-small mb-3">No truck types configured yet.</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-plum text-white text-small font-semibold
                       hover:bg-rose transition-colors duration-150"
          >
            <Plus size={16} /> Add Truck Type
          </button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && items.length > 0 && !editingOrder && (
        <p className="text-center py-8 text-slate text-small">No truck types matching &ldquo;{search}&rdquo;</p>
      )}

      {/* List */}
      {!loading && !error && filtered.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
            {filtered.length} type{filtered.length !== 1 ? 's' : ''}
            {search && ` matching "${search}"`}
          </p>
          <div className="flex flex-col gap-2">
            {filtered.map(item => (
              <TruckTypeCard
                key={item.id}
                item={item}
                editingOrder={editingOrder}
                isDragOver={dragOverId === item.id}
                isDragging={draggedId === item.id}
                onEdit={() => setEditingItem(item)}
                onDelete={() => setDeletingItem(item)}
                onDragStart={e => handleDragStart(e, item.id)}
                onDragOver={e => handleDragOver(e, item.id)}
                onDrop={e => handleDrop(e, item.id)}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        </>
      )}

      </>)}

      {/* ── Makes & Models Section ──────────────────────────────────────── */}
      {!loading && !error && !editingOrder && (
        <MakesModelsSection truckTypes={items} />
      )}

      {/* Truck Type Modals */}
      {showAddForm && (
        <TruckTypeForm
          onClose={() => setShowAddForm(false)}
          onSave={handleCreate}
        />
      )}

      {editingItem && (
        <TruckTypeForm
          initial={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleUpdate}
        />
      )}

      <ConfirmModal
        isOpen={!!deletingItem}
        title="Delete Truck Type"
        message={deletingItem ? `Are you sure you want to delete "${deletingItem.name}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeletingItem(null)}
      />
    </div>
  );
}

// ── Page Wrapper ──────────────────────────────────────────────────────────

export function FleetDefaultsPage() {
  const { currentUser, loading } = useUserProfile();

  if (loading) return <LoadingSpinner />;

  if (!currentUser?.isPlatformAdmin && IS_FIREBASE_CONFIGURED) {
    return (
      <div className="px-4 pt-6">
        <p className="text-slate">Platform Admin access required.</p>
      </div>
    );
  }

  return <FleetDefaultsContent />;
}
